import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import {
  Bold as BoldIcon, Italic as ItalicIcon, List, ListOrdered, Undo2, Redo2,
  Save, FilePlus2, CheckCircle2, Trash2, FileText, Signature, ExternalLink,
} from "lucide-react";

const Toolbar = ({ editor }) => {
  if (!editor) return null;
  const Btn = ({ onClick, active, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`p-2 rounded-lg border transition ${active ? "bg-white/15 border-white/30 text-white" : "border-white/10 text-slate-300 hover:bg-white/5"}`}
    >
      {children}
    </button>
  );
  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} testid="waiver-tb-bold"><BoldIcon className="w-4 h-4" /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} testid="waiver-tb-italic"><ItalicIcon className="w-4 h-4" /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} testid="waiver-tb-h2"><span className="text-xs font-bold">H2</span></Btn>
      <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} testid="waiver-tb-h3"><span className="text-xs font-bold">H3</span></Btn>
      <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} testid="waiver-tb-ul"><List className="w-4 h-4" /></Btn>
      <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} testid="waiver-tb-ol"><ListOrdered className="w-4 h-4" /></Btn>
      <Btn onClick={() => editor.chain().focus().undo().run()} testid="waiver-tb-undo"><Undo2 className="w-4 h-4" /></Btn>
      <Btn onClick={() => editor.chain().focus().redo().run()} testid="waiver-tb-redo"><Redo2 className="w-4 h-4" /></Btn>
    </div>
  );
};

export default function AdminWaiver() {
  const [versions, setVersions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [title, setTitle] = useState("Standard Liability Waiver");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit],
    content: "<p>By signing below, I acknowledge the inherent risks of using simulator equipment and release <strong>Pit Lane Lounge</strong> from liability for any injury or damage arising from my session.</p>",
    editorProps: { attributes: { class: "prose prose-invert max-w-none min-h-[280px] outline-none px-4 py-3" } },
  });

  const currentVersion = useMemo(() => versions.find((v) => v.published) || null, [versions]);

  const load = async () => {
    setLoading(true);
    try {
      const [v, s] = await Promise.all([
        apiClient.get("/admin/waiver/versions"),
        apiClient.get("/admin/waiver-settings"),
      ]);
      setVersions(v.data || []);
      setSettings(s.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to load waiver"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (currentVersion && editor) {
      setTitle(currentVersion.title || "");
      editor.commands.setContent(currentVersion.body_html || "");
    }
  }, [currentVersion?.id]);

  const publishNew = async () => {
    if (!editor) return;
    const html = editor.getHTML();
    if (!title.trim()) return toast.error("Title is required");
    if (html.length < 20) return toast.error("Waiver body is too short");
    if (!window.confirm("Publishing a new version will REQUIRE every guest to re-sign. Continue?")) return;
    setSaving(true);
    try {
      await apiClient.post("/admin/waiver/versions", { title, body_html: html, publish: true });
      toast.success("Waiver published — all guests must re-sign.");
      load();
    } catch (e) { toast.error(getErrorMessage(e, "Publish failed")); }
    finally { setSaving(false); }
  };

  const saveDraft = async () => {
    if (!editor) return;
    setSaving(true);
    try {
      await apiClient.post("/admin/waiver/versions", { title, body_html: editor.getHTML(), publish: false });
      toast.success("Draft saved");
      load();
    } catch (e) { toast.error(getErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const publishVersion = async (id) => {
    if (!window.confirm("Make this the active waiver? All guests will need to re-sign.")) return;
    try { await apiClient.post(`/admin/waiver/versions/${id}/publish`); toast.success("Published"); load(); }
    catch (e) { toast.error(getErrorMessage(e, "Publish failed")); }
  };

  const deleteVersion = async (id) => {
    if (!window.confirm("Delete this draft version?")) return;
    try { await apiClient.delete(`/admin/waiver/versions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(getErrorMessage(e, "Delete failed")); }
  };

  const updateSettings = async (patch) => {
    try {
      const r = await apiClient.put("/admin/waiver-settings", patch);
      setSettings(r.data);
      toast.success("Settings updated");
    } catch (e) { toast.error(getErrorMessage(e, "Update failed")); }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8 space-y-6 max-w-6xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3"><FileText className="w-8 h-8 text-pink-400" /> Waiver</h1>
            <p className="text-slate-400 mt-1">Editable liability waiver — publishing a new version forces every guest to sign again.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/waiver/signatures" className="chip" data-testid="waiver-signatures-link">
              <Signature className="w-4 h-4" /> Signatures
            </Link>
            <Link to="/admin/qr-codes" className="chip" data-testid="waiver-qr-link">
              <ExternalLink className="w-4 h-4" /> QR Codes
            </Link>
          </div>
        </div>

        {settings && (
          <Card className="p-5 bg-white/5 border-white/10 space-y-4">
            <h3 className="text-white font-semibold">Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Signature validity window</Label>
                <div className="flex gap-2 mt-1.5">
                  {[3, 6, 12].map((m) => (
                    <button key={m} type="button"
                      onClick={() => updateSettings({ validity_months: m })}
                      data-testid={`waiver-validity-${m}`}
                      className={`px-4 py-2 rounded-lg border font-semibold text-sm ${settings.validity_months === m ? "bg-pink-500/20 border-pink-500/50 text-white" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
                      {m} months
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Session auto-close grace (minutes past booking end)</Label>
                <Input type="number" min={0} max={180} value={settings.session_grace_minutes}
                  onChange={(e) => setSettings({ ...settings, session_grace_minutes: Number(e.target.value) })}
                  onBlur={(e) => updateSettings({ session_grace_minutes: Number(e.target.value) })}
                  data-testid="waiver-grace-input"
                  className="mt-1.5 bg-black/30 border-white/10 text-white" />
              </div>
            </div>
          </Card>
        )}

        <Card className="p-5 bg-white/5 border-white/10">
          <h3 className="text-white font-semibold mb-3">Edit current waiver</h3>
          <Label className="text-slate-300">Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
            data-testid="waiver-title-input"
            className="mb-4 mt-1.5 bg-black/30 border-white/10 text-white" />
          <Label className="text-slate-300">Body</Label>
          <div className="mt-1.5 rounded-xl border border-white/10 bg-black/30" data-testid="waiver-editor">
            <div className="border-b border-white/10 p-2"><Toolbar editor={editor} /></div>
            <EditorContent editor={editor} />
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={saveDraft} disabled={saving} variant="outline" data-testid="waiver-save-draft-btn">
              <Save className="w-4 h-4 mr-2" /> Save as draft
            </Button>
            <Button onClick={publishNew} disabled={saving} className="bg-gradient-to-r from-pink-500 to-violet-500" data-testid="waiver-publish-btn">
              <FilePlus2 className="w-4 h-4 mr-2" /> Publish new version
            </Button>
          </div>
        </Card>

        <Card className="p-5 bg-white/5 border-white/10">
          <h3 className="text-white font-semibold mb-3">Version history</h3>
          {loading ? <div className="text-slate-400">Loading…</div> : (
            <div className="space-y-2">
              {versions.map((v) => (
                <div key={v.id} data-testid={`waiver-version-${v.version}`}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl border border-white/10 bg-black/20">
                  <div className="min-w-0">
                    <div className="text-white font-semibold flex items-center gap-2">
                      v{v.version} · {v.title}
                      {v.published && <span className="text-xs font-bold text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> ACTIVE</span>}
                    </div>
                    <div className="text-xs text-slate-500">
                      {new Date(v.created_at).toLocaleString()} · {v.signature_count} signatures
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!v.published && (
                      <>
                        <button onClick={() => publishVersion(v.id)} className="chip" data-testid={`waiver-publish-${v.version}`}>Publish</button>
                        {v.signature_count === 0 && (
                          <button onClick={() => deleteVersion(v.id)} className="chip text-red-400" data-testid={`waiver-delete-${v.version}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
              {versions.length === 0 && <div className="text-slate-500 text-sm">No versions yet — publish your first waiver above.</div>}
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
